import React, { Fragment, useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Page from "../../components/Page";
import { IconCheck, IconChevronDown, IconPencil, IconPlus, IconTrash, IconEye, IconEyeOff, IconQrcode, IconInfoCircle, IconBolt } from "@tabler/icons-react";
import { iconStroke } from "../../config/config";
import { PAYMENT_ICONS } from "../../config/payment_icons";
import { addNewPaymentType, deletePaymentType, togglePaymentType, updatePaymentType, usePaymentTypes } from "../../controllers/settings.controller";
import toast from "react-hot-toast";
import { mutate } from "swr";
import { useTheme } from "../../contexts/ThemeContext";
import { Listbox, Transition } from '@headlessui/react';
import { QRCodeSVG } from 'qrcode.react';

function QRConfigEditor({ value, onChange }) {
  const [mode, setMode] = useState('none');
  const [staticLink, setStaticLink] = useState('');
  const [showSecret, setShowSecret] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [dynamicConfig, setDynamicConfig] = useState({
    type: 'dynamic',
    provider: 'fonepay',
    merchant_code: '',
    secret_key: '',
    merchant_id: '',
    terminal_id: '',
    pan_no: '',
    username: '',
    password: '',
    is_uat: false,
    remarks: 'Restaurant Bill'
  });

  // Sync state when value changes (e.g. opening edit modal for different payment types)
  useEffect(() => {
    if (!value) {
      setMode('none');
      setStaticLink('');
      setDynamicConfig({
        type: 'dynamic',
        provider: 'fonepay',
        merchant_code: '',
        secret_key: '',
        merchant_id: '',
        terminal_id: '',
        pan_no: '',
        username: '',
        password: '',
        is_uat: false,
        remarks: 'Restaurant Bill'
      });
      return;
    }

    try {
      const parsed = JSON.parse(value);
      if (parsed && parsed.type === 'dynamic') {
        setMode('dynamic');
        setDynamicConfig({
          type: 'dynamic',
          provider: parsed.provider || 'fonepay',
          merchant_code: parsed.merchant_code || '',
          secret_key: parsed.secret_key || '',
          merchant_id: parsed.merchant_id || '',
          terminal_id: parsed.terminal_id || '',
          pan_no: parsed.pan_no || '',
          username: parsed.username || '',
          password: parsed.password || '',
          is_uat: !!parsed.is_uat,
          remarks: parsed.remarks || 'Restaurant Bill'
        });
      } else if (parsed && parsed.type === 'static') {
        setMode('static');
        setStaticLink(parsed.qr_string || parsed.link || '');
      } else {
        setMode('static');
        setStaticLink(typeof value === 'string' ? value : JSON.stringify(value));
      }
    } catch {
      // Plain string link or payload
      setMode('static');
      setStaticLink(value || '');
    }
  }, [value]);

  const handleModeChange = (newMode) => {
    setMode(newMode);
    if (newMode === 'none') {
      onChange('');
    } else if (newMode === 'static') {
      onChange(JSON.stringify({ type: 'static', qr_string: staticLink.trim() }));
    } else if (newMode === 'dynamic') {
      onChange(JSON.stringify({ ...dynamicConfig, type: 'dynamic' }));
    }
  };

  const handleStaticLinkChange = (val) => {
    setStaticLink(val);
    onChange(JSON.stringify({ type: 'static', qr_string: val.trim() }));
  };

  const updateDynamicConfig = (updates) => {
    const newConfig = { ...dynamicConfig, ...updates, type: 'dynamic' };
    setDynamicConfig(newConfig);
    onChange(JSON.stringify(newConfig));
  };

  return (
    <div className="mt-4 p-4 border border-restro-border-green rounded-xl bg-gray-50/70 dark:bg-zinc-900/70">
      <div className="flex items-center justify-between mb-3">
        <label className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
          <IconQrcode size={16} className="text-restro-green" /> QR Code Setup
        </label>
        {mode === 'dynamic' && (
          <span className="badge badge-error badge-sm text-[10px] font-bold text-white uppercase tracking-wider">
            Fonepay Dynamic
          </span>
        )}
        {mode === 'static' && (
          <span className="badge badge-neutral badge-sm text-[10px] font-bold uppercase tracking-wider">
            Static Link
          </span>
        )}
      </div>

      {/* Mode Selector Tabs */}
      <div className="grid grid-cols-3 gap-2 p-1 bg-gray-200/60 dark:bg-zinc-800 rounded-xl mb-4">
        <button
          type="button"
          onClick={() => handleModeChange('none')}
          className={`py-1.5 text-xs font-bold rounded-lg transition ${
            mode === 'none'
              ? 'bg-white dark:bg-zinc-900 text-gray-800 dark:text-white shadow-xs'
              : 'text-gray-500 hover:text-gray-800 dark:hover:text-gray-200'
          }`}
        >
          No QR
        </button>
        <button
          type="button"
          onClick={() => handleModeChange('static')}
          className={`py-1.5 text-xs font-bold rounded-lg transition ${
            mode === 'static'
              ? 'bg-white dark:bg-zinc-900 text-restro-green shadow-xs'
              : 'text-gray-500 hover:text-gray-800 dark:hover:text-gray-200'
          }`}
        >
          Static QR Link
        </button>
        <button
          type="button"
          onClick={() => handleModeChange('dynamic')}
          className={`py-1.5 text-xs font-bold rounded-lg transition ${
            mode === 'dynamic'
              ? 'bg-white dark:bg-zinc-900 text-red-600 dark:text-red-400 shadow-xs'
              : 'text-gray-500 hover:text-gray-800 dark:hover:text-gray-200'
          }`}
        >
          Dynamic QR
        </button>
      </div>

      {/* Static QR Form */}
      {mode === 'static' && (
        <div className="space-y-3">
          <div>
            <label className="text-xs font-bold text-gray-600 dark:text-gray-300 block mb-1">
              Static Payment Link / QR Data / URL
            </label>
            <input
              type="text"
              className="w-full text-xs rounded-lg px-3 py-2 border border-restro-border-green focus:outline-restro-border-green dark:bg-black font-mono"
              value={staticLink}
              onChange={(e) => handleStaticLinkChange(e.target.value)}
              placeholder="e.g. https://esewa.com.np/qr/... or fonepay static string or upi://pay?..."
            />
            <p className="text-[11px] text-gray-400 mt-1">
              In POS, selecting this payment method will generate and show this static QR for the customer to scan.
            </p>
          </div>

          {/* Live Preview */}
          {staticLink.trim() && (
            <div className="p-3 bg-white dark:bg-black rounded-xl border border-gray-200 dark:border-zinc-800 flex items-center gap-4">
              <div className="p-2 bg-white rounded-lg border border-gray-100 shadow-xs">
                <QRCodeSVG value={staticLink.trim()} size={90} level="M" />
              </div>
              <div className="flex-1">
                <p className="text-xs font-bold text-gray-700 dark:text-gray-200">Live QR Preview</p>
                <p className="text-[10px] text-gray-400 break-all line-clamp-2 mt-0.5">{staticLink}</p>
                <span className="text-[10px] text-emerald-600 dark:text-emerald-400 font-semibold mt-1 inline-flex items-center gap-1">
                  <IconCheck size={12} /> Ready to scan at POS checkout
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Dynamic QR Form (Fonepay Nepal) */}
      {mode === 'dynamic' && (
        <div className="space-y-3">
          {/* Provider & Environment */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div>
              <label className="text-[11px] font-bold text-gray-500 uppercase tracking-wider block mb-1">Provider</label>
              <select
                className="w-full text-xs rounded-lg px-3 py-2 border border-restro-border-green focus:outline-restro-border-green dark:bg-black"
                value={dynamicConfig.provider || 'fonepay'}
                onChange={(e) => updateDynamicConfig({ provider: e.target.value })}
              >
                <option value="fonepay">Fonepay (Nepal)</option>
                <option value="esewa">eSewa (Nepal)</option>
              </select>
            </div>

            <div>
              <label className="text-[11px] font-bold text-gray-500 uppercase tracking-wider block mb-1">Environment</label>
              <select
                className="w-full text-xs rounded-lg px-3 py-2 border border-restro-border-green focus:outline-restro-border-green dark:bg-black font-semibold"
                value={dynamicConfig.is_uat ? 'uat' : 'prod'}
                onChange={(e) => updateDynamicConfig({ is_uat: e.target.value === 'uat' })}
              >
                <option value="prod">Production (Live Gateway)</option>
                <option value="uat">UAT / Sandbox (Testing)</option>
              </select>
            </div>
          </div>

          {dynamicConfig.provider === 'fonepay' && (
            <>
              {/* Info Banner */}
              <div className="p-2.5 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900/50 rounded-xl flex items-start gap-2">
                <IconBolt size={18} className="text-red-600 shrink-0 mt-0.5" />
                <p className="text-[11px] text-red-800 dark:text-red-300">
                  <strong>Fonepay Dynamic QR:</strong> When selected in POS, this directly requests Fonepay with the bill amount to display a unique, transaction-locked QR code.
                </p>
              </div>

              {/* Row 1: Merchant Code & Secret Key */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <label className="text-xs font-bold text-gray-700 dark:text-gray-200 block mb-1">
                    Merchant Code <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    className="w-full text-xs rounded-lg px-3 py-2 border border-restro-border-green focus:outline-restro-border-green dark:bg-black font-mono"
                    value={dynamicConfig.merchant_code || ''}
                    onChange={(e) => updateDynamicConfig({ merchant_code: e.target.value })}
                    placeholder="Provided by Fonepay"
                  />
                </div>

                <div>
                  <label className="text-xs font-bold text-gray-700 dark:text-gray-200 block mb-1">
                    Secret Key <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type={showSecret ? "text" : "password"}
                      className="w-full text-xs rounded-lg pl-3 pr-8 py-2 border border-restro-border-green focus:outline-restro-border-green dark:bg-black font-mono"
                      value={dynamicConfig.secret_key || ''}
                      onChange={(e) => updateDynamicConfig({ secret_key: e.target.value })}
                      placeholder="HMAC Secret Key"
                    />
                    <button
                      type="button"
                      onClick={() => setShowSecret(!showSecret)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showSecret ? <IconEyeOff size={15} /> : <IconEye size={15} />}
                    </button>
                  </div>
                </div>
              </div>

              {/* Row 2: Merchant ID & Terminal ID */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <label className="text-xs font-semibold text-gray-600 dark:text-gray-300 block mb-1">
                    Merchant ID (MID)
                  </label>
                  <input
                    type="text"
                    className="w-full text-xs rounded-lg px-3 py-2 border border-restro-border-green focus:outline-restro-border-green dark:bg-black font-mono"
                    value={dynamicConfig.merchant_id || ''}
                    onChange={(e) => updateDynamicConfig({ merchant_id: e.target.value })}
                    placeholder="Fonepay MID"
                  />
                </div>

                <div>
                  <label className="text-xs font-semibold text-gray-600 dark:text-gray-300 block mb-1">
                    Terminal ID (TID)
                  </label>
                  <input
                    type="text"
                    className="w-full text-xs rounded-lg px-3 py-2 border border-restro-border-green focus:outline-restro-border-green dark:bg-black font-mono"
                    value={dynamicConfig.terminal_id || ''}
                    onChange={(e) => updateDynamicConfig({ terminal_id: e.target.value })}
                    placeholder="e.g. POS01 or Counter-1"
                  />
                </div>
              </div>

              {/* Row 3: Fonepay PAN No & Username */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <label className="text-xs font-semibold text-gray-600 dark:text-gray-300 block mb-1">
                    Fonepay PAN No
                  </label>
                  <input
                    type="text"
                    className="w-full text-xs rounded-lg px-3 py-2 border border-restro-border-green focus:outline-restro-border-green dark:bg-black font-mono"
                    value={dynamicConfig.pan_no || ''}
                    onChange={(e) => updateDynamicConfig({ pan_no: e.target.value })}
                    placeholder="Registered PAN / Tax ID"
                  />
                </div>

                <div>
                  <label className="text-xs font-semibold text-gray-600 dark:text-gray-300 block mb-1">
                    Username
                  </label>
                  <input
                    type="text"
                    className="w-full text-xs rounded-lg px-3 py-2 border border-restro-border-green focus:outline-restro-border-green dark:bg-black font-mono"
                    value={dynamicConfig.username || ''}
                    onChange={(e) => updateDynamicConfig({ username: e.target.value })}
                    placeholder="Merchant API Username"
                  />
                </div>
              </div>

              {/* Row 4: Password & Remarks */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <label className="text-xs font-semibold text-gray-600 dark:text-gray-300 block mb-1">
                    Password <span className="text-[10px] text-gray-400 font-normal">(if provided)</span>
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      className="w-full text-xs rounded-lg pl-3 pr-8 py-2 border border-restro-border-green focus:outline-restro-border-green dark:bg-black font-mono"
                      value={dynamicConfig.password || ''}
                      onChange={(e) => updateDynamicConfig({ password: e.target.value })}
                      placeholder="API Password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <IconEyeOff size={15} /> : <IconEye size={15} />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="text-xs font-semibold text-gray-600 dark:text-gray-300 block mb-1">
                    Remarks / Purpose
                  </label>
                  <input
                    type="text"
                    className="w-full text-xs rounded-lg px-3 py-2 border border-restro-border-green focus:outline-restro-border-green dark:bg-black"
                    value={dynamicConfig.remarks || ''}
                    onChange={(e) => updateDynamicConfig({ remarks: e.target.value })}
                    placeholder="Restaurant Bill Payment"
                  />
                </div>
              </div>
            </>
          )}

          {dynamicConfig.provider === 'esewa' && (
            <div>
              <label className="text-xs font-bold text-gray-700 dark:text-gray-200 block mb-1">eSewa Merchant Code</label>
              <input
                type="text"
                className="w-full text-xs rounded-lg px-3 py-2 border border-restro-border-green focus:outline-restro-border-green dark:bg-black font-mono"
                value={dynamicConfig.merchant_id || ''}
                onChange={(e) => updateDynamicConfig({ merchant_id: e.target.value })}
                placeholder="e.g. EPAYTEST"
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}


export default function PaymentTypesPage() {
  const { t } = useTranslation();
  const paymentTypeAddRef = useRef();

  const paymentTypeIdUpdateRef = useRef();
  const paymentTypeTitleUpdateRef = useRef();
  const paymentTypeIsActiveUpdateRef = useRef();

  const [selectedIcon, setSelectedIcon] = useState();
  
  const [qrConfigAdd, setQrConfigAdd] = useState("");
  const [qrConfigUpdate, setQrConfigUpdate] = useState("");

  const { theme } = useTheme();


  const { APIURL, data: paymentTypes, error, isLoading } = usePaymentTypes();

  if (isLoading) {
    return <Page className="px-8 py-6">{t('payment_types.please_wait')}</Page>;
  }

  if (error) {
    console.error(error);
    return <Page className="px-8 py-6">{t('payment_types.error_loading_data')}</Page>;
  }

  const btnDelete = async (id) => {
    const isConfirm = window.confirm(t('payment_types.delete_confirm'));

    if(!isConfirm) {
      return;
    }

    try {
      toast.loading(t('payment_types.please_wait'));
      const res = await deletePaymentType(id);

      if(res.status == 200) {
        await mutate(APIURL);
        toast.dismiss();
        toast.success(res.data.message);
      }
    } catch (error) {
      const message = error?.response?.data?.message || t('payment_types.something_went_wrong');
      console.error(error);

      toast.dismiss();
      toast.error(message);
    }
  };

  const btnShowUpdate = (id, title, isActive, icon, paymentType) => {
    paymentTypeIdUpdateRef.current.value = id;
    paymentTypeIsActiveUpdateRef.current.checked = isActive;
    paymentTypeTitleUpdateRef.current.value = title;
    setQrConfigUpdate(paymentType.qr_config || "");
    setSelectedIcon(icon);
    setTimeout(()=>{document.getElementById('modal-update').showModal();}, 100);
  };

  const btnUpdate = async () => {
    const id = paymentTypeIdUpdateRef.current.value
    const title = paymentTypeTitleUpdateRef.current.value
    const isActive = paymentTypeIsActiveUpdateRef.current.checked
    const qrConfig = qrConfigUpdate

    if(!title) {
      toast.error(t('payment_types.payment_type_title'));
      return;
    }

    try {
      toast.loading(t('payment_types.please_wait'));
      const res = await updatePaymentType(id, title, isActive, selectedIcon, qrConfig);

      if(res.status == 200) {
        paymentTypeIdUpdateRef.current.value = null;
        paymentTypeIsActiveUpdateRef.current.checked = null;
        paymentTypeTitleUpdateRef.current.value = null;

        await mutate(APIURL);
        setSelectedIcon(undefined);
        toast.dismiss();
        toast.success(res.data.message);
      }
    } catch (error) {
      const message = error?.response?.data?.message || t('payment_types.something_went_wrong');
      console.error(error);

      toast.dismiss();
      toast.error(message);
    }
  };

  const toggle = async (id, value) => {
    try {
      toast.loading(t('payment_types.please_wait'));
      const res = await togglePaymentType(id, value);

      if(res.status == 200) {
        await mutate(APIURL);
        toast.dismiss();
        toast.success(res.data.message);
      }
    } catch (error) {
      const message = error?.response?.data?.message || t('payment_types.something_went_wrong');
      console.error(error);

      toast.dismiss();
      toast.error(message);
    }
  };

  async function btnAdd() {
    const paymentType = paymentTypeAddRef.current.value;
    const qrConfig = qrConfigAdd;

    if(!paymentType) {
      toast.error(t('payment_types.payment_type_title'));
      return;
    }

    try {
      toast.loading(t('payment_types.please_wait'));
      const res = await addNewPaymentType(paymentType, true, selectedIcon, qrConfig);

      if(res.status == 200) {
        paymentTypeAddRef.current.value = "";
        setQrConfigAdd("");
        await mutate(APIURL);
        setSelectedIcon(undefined);
        toast.dismiss();
        toast.success(res.data.message);
      }
    } catch (error) {
      const message = error?.response?.data?.message || t('payment_types.something_went_wrong');
      console.error(error);

      toast.dismiss();
      toast.error(message);
    }
  };

  return (
    <Page className="px-8 py-6">
      <div className="flex items-center gap-6">
        <h3 className="text-3xl font-light">{t('payment_types.title')}</h3>
        <button
          onClick={() => document.getElementById("modal-add").showModal()}
          className = "text-sm rounded-lg border transition active:scale-95 hover:shadow-lg px-2 py-1 flex items-center gap-1 text-restro-text bg-restro-gray border-restro-border-green hover:bg-restro-button-hover"
        >
          <IconPlus size={22} stroke={iconStroke} /> {t('payment_types.new')}
        </button>
      </div>

      <div className="mt-8 w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {paymentTypes.map((paymentType, index) => {
          const { id, title, is_active, icon, qr_config } = paymentType;

          return (
            <div key={index} className='flex flex-col justify-between transition active:scale-95 gap-4 px-4 py-3 border rounded-2xl text-restro-text border-restro-border-green'>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {icon && <div className='w-12 h-12 rounded-full flex items-center justify-center bg-restro-bg-gray'>{PAYMENT_ICONS[icon]}</div>} <p>{title}</p>
                </div>
                {qr_config && <div className="badge badge-success badge-xs">QR</div>}
              </div>
              <div className="mt-4 flex gap-2 items-center">
                <div className="flex flex-1 items-center gap-2">
                  <button
                    onClick={() => {
                      btnShowUpdate(id, title, is_active, icon, paymentType);
                    }}
                    className='w-8 h-8 rounded-full flex items-center justify-center text-restro-text hover:bg-restro-button-hover'
                  >
                    <IconPencil stroke={iconStroke} />
                  </button>
                  <button
                    onClick={() => {
                      btnDelete(id);
                    }}
                    className='w-8 h-8 rounded-full flex items-center justify-center text-restro-red transition active:scale-95 hover:bg-restro-button-hover'
                  >
                    <IconTrash stroke={iconStroke} />
                  </button>
                </div>

                {/* switch */}
                <label className="relative flex cursor-pointer no-drag">
                  <input
                    onChange={(e) => toggle(id, e.target.checked)}
                    defaultChecked={is_active}
                    checked={is_active}
                    type="checkbox"
                    className="sr-only peer"
                  />
                  <div className={`w-11 h-6 rounded-full peer peer-checked:after:translate-x-full  after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-restro-bg-gray after:border after:rounded-full after:h-5 after:w-5 after:transition-all bg-restro-checkbox peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-restro-gray peer-checked:bg-restro-green peer-checked:after:border-restro-border-green`}></div>
                </label>
                {/* switch */}
              </div>
            </div>
          );
        })}
      </div>

      <dialog id="modal-add" className="modal modal-bottom sm:modal-middle">
        <div className='modal-box overflow-y-visible border border-restro-border-green dark:rounded-2xl'>
          <h3 className="font-bold text-lg">{t('payment_types.add_new_payment_type')}</h3>

          <div className="my-4">
            <label
              htmlFor="paymentType"
              className="mb-1 block text-gray-500 text-sm"
            >
              {t('payment_types.payment_type_title')}
            </label>
            <input
              ref={paymentTypeAddRef}
              type="text"
              name="paymentType"
              className='text-sm w-full rounded-lg px-4 py-2 border border-restro-border-green dark:bg-black focus:outline-restro-border-green'
              placeholder={t('payment_types.enter_payment_type')}
            />

            <QRConfigEditor value={qrConfigAdd} onChange={setQrConfigAdd} />

            <label
              htmlFor="icon"
              className="mb-1 mt-4 block text-gray-500 text-sm"
            >
              {t('payment_types.icon')}
            </label>
            <Listbox value={selectedIcon} onChange={setSelectedIcon}>
              <div className="relative mt-1">
                <Listbox.Button 
                className='relative w-full cursor-default text-left sm:text-sm border border-restro-border-green dark:bg-black rounded-lg px-4 py-2 focus:border-restro-border-green focus:outline-none'>

                  <span className="truncate flex items-center gap-2">
                    {PAYMENT_ICONS[selectedIcon]}{" "}
                    {selectedIcon
                      ? new String(selectedIcon).toUpperCase()
                      : t('payment_types.select_icon')}
                  </span>
                  <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                    <IconChevronDown
                      className="text-gray-400"
                      aria-hidden="true"
                    />
                  </span>
                </Listbox.Button>
                <Transition
                  as={Fragment}
                  leave="transition ease-in duration-100"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <Listbox.Options className='absolute mt-1 max-h-60 w-full overflow-auto rounded-md py-1 text-base shadow-lg focus:outline-none sm:text-sm grid grid-cols-3 gap-4 z-50 border border-restro-gray bg-background'>
                    {Object.entries(PAYMENT_ICONS).map((icon) => (
                      <Listbox.Option
                        key={icon[0]}
                        className={({ active }) =>
                          `relative cursor-default select-none w-full h-full flex items-center justify-center py-4 rounded-lg hover:bg-restro-gray hover:text-foreground  ${
                            active
                              ? (theme === 'black' ? 'bg-restro-bg-hover-dark-mode/70 text-white' : 'bg-restro-green/50 text-restro-green')
                              : (theme === 'black' ? 'text-white' : '')
                          }`
                        }
                        value={icon[0]}
                      >
                        {({ selected }) => (
                          <>
                            <span
                              className={`block truncate ${
                                selected
                                  ? "font-medium text-restro-green"
                                  : "font-normal"
                              }`}
                            >
                              {icon[1]}
                            </span>
                            {selected ? (
                              <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-restro-green">
                                <IconCheck
                                  stroke={iconStroke}
                                  aria-hidden="true"
                                />
                              </span>
                            ) : null}
                          </>
                        )}
                      </Listbox.Option>
                    ))}
                  </Listbox.Options>
                </Transition>
              </div>
            </Listbox>
          </div>

          <div className="modal-action">
            <form method="dialog">
              {/* if there is a button in form, it will close the modal */}
              <button className='rounded-xl transition active:scale-95 border hover:shadow-lg px-4 py-3 text-gray-500 border-restro-border-green bg-restro-card-bg hover:bg-restro-button-hover text-restro-text'>
                {t('payment_types.close')}
              </button>
              <button
                onClick={() => {
                  btnAdd();
                }}
                className=' rounded-xl transition active:scale-95 hover:shadow-lg px-4 py-3 text-white ml-3  border border-restro-border-green bg-restro-green hover:bg-restro-green-button-hover'
              >
                {t('payment_types.save')}
              </button>
            </form>
          </div>
        </div>
      </dialog>

      <dialog id="modal-update" className="modal modal-bottom sm:modal-middle">
        <div className='modal-box overflow-y-visible border border-restro-border-green dark:rounded-2xl'>
          <h3 className="font-bold text-lg">{t('payment_types.update_payment_type')}</h3>

          <div className="mt-4">
            <input type="hidden" ref={paymentTypeIdUpdateRef} />
            <label
              htmlFor="paymentTypeUpdate"
              className="mb-1 block text-gray-500 text-sm"
            >
              {t('payment_types.payment_type_title')}
            </label>
            <input
              ref={paymentTypeTitleUpdateRef}
              type="text"
              name="paymentTypeUpdate"
              className='text-sm w-full rounded-lg px-4 py-2 border border-restro-border-green dark:bg-black focus:outline-restro-border-green'
              placeholder={t('payment_types.enter_payment_type')}
            />
          </div>

          <QRConfigEditor value={qrConfigUpdate} onChange={setQrConfigUpdate} />

          <div className="mt-4 flex items-center justify-between w-full">
            <label
              htmlFor="isActivePaymentUpdate"
              className="block text-gray-500 text-sm"
            >
              {t('payment_types.active')}
            </label>
            {/* switch */}
            <label className="relative flex items-center cursor-pointer no-drag">
              <input
                ref={paymentTypeIsActiveUpdateRef}
                type="checkbox"
                className="sr-only peer"
              />
              <div className={`w-11 h-6 rounded-full peer peer-checked:after:translate-x-full  after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-gray-100 after:border-restro-bg-gray after:border after:rounded-full after:h-5 after:w-5 after:transition-all bg-restro-checkbox peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-restro-ring-light peer-checked:bg-restro-green peer-checked:after:border-restro-border-green`}></div>
            </label>
            {/* switch */}
          </div>

          <div className="mt-4">
            <label
              htmlFor="icon"
              className="mb-1 mt-4 block text-gray-500 text-sm"
            >
              {t('payment_types.icon')}
            </label>
            <Listbox value={selectedIcon} onChange={setSelectedIcon}>
              <div className="relative mt-1">
                <Listbox.Button className='relative w-full cursor-default text-left sm:text-sm border border-restro-border-green dark:bg-black rounded-lg px-4 py-2 focus:border-restro-border-green focus:outline-none'>
                  <span className="truncate flex items-center gap-2">
                    {PAYMENT_ICONS[selectedIcon]}{" "}
                    {selectedIcon
                      ? new String(selectedIcon).toUpperCase()
                      : t('payment_types.select_icon')}
                  </span>
                  <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                    <IconChevronDown
                      className="text-gray-400"
                      aria-hidden="true"
                    />
                  </span>
                </Listbox.Button>
                <Transition
                  as={Fragment}
                  leave="transition ease-in duration-100"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                   <Listbox.Options className='absolute mt-1 max-h-60 w-full overflow-auto rounded-md py-1 text-base shadow-lg focus:outline-none sm:text-sm grid grid-cols-3 gap-4 z-50 border border-restro-gray bg-background'>
                    {Object.entries(PAYMENT_ICONS).map((icon) => (
                      <Listbox.Option
                        key={icon[0]}
                        className={({ active }) =>
                          `relative cursor-default select-none w-full h-full flex items-center justify-center py-4 rounded-lg hover:bg-restro-gray hover:text-foreground  ${
                            active
                              ? (theme === 'black' ? 'bg-restro-bg-hover-dark-mode/70 text-white' : 'bg-restro-green/50 text-restro-green')
                              : (theme === 'black' ? 'text-white' : '')
                          }`
                        }
                        value={icon[0]}
                      >
                        {({ selected }) => (
                          <>
                            <span
                              className={`block truncate ${
                                selected
                                  ? "font-medium text-restro-green"
                                  : "font-normal"
                              }`}
                            >
                              {icon[1]}
                            </span>
                            {selected ? (
                              <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-restro-green">
                                <IconCheck
                                  stroke={iconStroke}
                                  aria-hidden="true"
                                />
                              </span>
                            ) : null}
                          </>
                        )}
                      </Listbox.Option>
                    ))}
                  </Listbox.Options>
                </Transition>
              </div>
            </Listbox>
          </div>

          <div className="modal-action">
            <form method="dialog">
              {/* if there is a button in form, it will close the modal */}
              <button className='btn transition active:scale-95 hover:shadow-lg px-4 py-3 flex-1 items-center justify-center align-center rounded-xl border border-restro-border-green bg-restro-card-bg hover:bg-restro-button-hover text-restro-text'>
                {t('payment_types.close')}
              </button>
              <button
                onClick={() => {
                  btnUpdate();
                }}
                className='rounded-xl transition active:scale-95 hover:shadow-lg px-4 py-3 text-white ml-3 border border-restro-border-green bg-restro-green hover:bg-restro-green-button-hover'
              >
                {t('payment_types.save')}
              </button>
            </form>
          </div>
        </div>
      </dialog>
    </Page>
  );
}
